import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { TurfPulseLogoSVG } from './TurfPulseLogo';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { SignOut, CreditCard, UserGear, Moon, Sun, Lock, List, X } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { LanguageSelector } from './LanguageSelector';

const ROUTE_FEATURE_MAP = {
  '/weed': 'weed_department',
  '/pest': 'pest_department',
  '/disease': 'disease_department',
  '/soil-analysis': 'soil_analysis',
  '/spray-forecast': 'spray_forecast',
  '/equipment': 'equipment_log',
  '/spreader-settings': 'spreader_settings',
  '/email-preferences': 'email_alerts',
  '/property': 'zone_management',
  '/field-guide': 'field_guide',
  '/calculator': 'mix_calculator',
};

export const DepartmentNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation(['nav', 'common']);
  const [open, setOpen] = useState(false);

  const navGroups = [
    {
      label: 'Overview',
      items: [
        { path: '/app', label: t('nav:dashboard', 'Dashboard'), icon: '📊' },
        { path: '/my-plan', label: 'My Plan', icon: '✅' },
        { path: '/inspections', label: 'Field Inspections', icon: '🔍' },
        // Property Measure hidden until Pro tier launch
        // { path: '/measure', label: 'Property Measure', icon: '📐' },
        { path: '/property', label: t('nav:property', 'My Property'), icon: '🏡' },
      ]
    },
    {
      label: 'Tracking',
      items: [
        { path: '/calendar', label: t('nav:calendar', 'Seasonal Calendar'), icon: '📅' },
        { path: '/spray-forecast', label: t('nav:sprayForecast', 'Spray Forecast'), icon: '🌬️' },
      ]
    },
    {
      label: 'Departments',
      items: [
        { path: '/pgr', label: t('nav:pgr', 'PGR Department'), icon: '🌱' },
        { path: '/weed', label: t('nav:weedControl', 'Weed Control'), icon: '🌿' },
        { path: '/pest', label: t('nav:pestManagement', 'Pest Management'), icon: '🐛' },
        { path: '/disease', label: t('nav:diseaseManagement', 'Disease Management'), icon: '⚠️' },
      ]
    },
    {
      label: 'Tools',
      items: [
        { path: '/calculator', label: 'Mix Calculator', icon: '🧮' },
        { path: '/soil-analysis', label: t('nav:soilAnalysis', 'Soil Analysis'), icon: '🧪' },
        { path: '/irrigation', label: t('nav:irrigation', 'Irrigation'), icon: '💧' },
        { path: '/equipment', label: t('nav:equipment', 'Equipment Log'), icon: '🔧' },
        { path: '/spreader-settings', label: t('nav:spreaderSettings', 'Spreader Settings'), icon: '⚙️' },
      ]
    },
    {
      label: 'Learn & Connect',
      items: [
        { path: '/learn', label: t('nav:learn', 'Learn'), icon: '🎓' },
        { path: '/fall-guide', label: 'Fall Overseeding Guide', icon: '🍂' },
        { path: '/field-guide', label: 'Field Guide', icon: '🎯' },
        { path: '/partners', label: t('nav:partners', 'Partners'), icon: '🤝' },
        { path: '/email-preferences', label: t('nav:emailPreferences', 'Email Alerts'), icon: '📧' },
      ]
    },
    {
      label: 'Community',
      items: [
        { path: '/merch', label: 'Merch Lab', icon: '👕' },
        { path: '/marketplace', label: 'Group Buys', icon: '🛒' },
      ]
    },
    // Contractor toolkit — shown to Pro users as "tools", and to everyone else as upsell to /pro
    ...(user?.subscription_tier === 'pro_business' || user?.role === 'admin'
      ? [{
          label: 'For Contractors',
          items: [
            { path: '/measure', label: 'Property Measure', icon: '📐' },
            { path: '/clients', label: 'Client Properties', icon: '👥' },
            { path: '/pro', label: 'Pro Overview', icon: '👑' },
          ]
        }]
      : [{
          label: 'For Contractors',
          items: [
            { path: '/pro', label: 'TurfPulse Pro', icon: '👑' },
          ]
        }]
    ),
  ];

  const isActive = (path) => {
    if (path === '/app') return location.pathname === '/app';
    return location.pathname.startsWith(path);
  };

  const isLocked = (path) => {
    const feature = ROUTE_FEATURE_MAP[path];
    if (!feature) return false;
    return !user?.allowed_features?.includes(feature);
  };

  const handleNav = (path) => {
    navigate(path);
    setOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const tierLabel = user?.subscription_tier === 'enthusiast_annual' ? 'Enthusiast'
    : user?.subscription_tier === 'homeowner_seasonal' ? 'Homeowner'
    : user?.subscription_tier === 'free' || !user?.subscription_tier ? 'Free'
    : '';

  // Find current page label for header
  const allItems = navGroups.flatMap(g => g.items);
  const currentPage = allItems.find(item => isActive(item.path));

  return (
    <header className="bg-background/70 backdrop-blur-xl border-b border-border shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-3 md:px-6 overflow-hidden">
        <div className="flex items-center justify-between h-14 min-w-0">
          {/* Left: Hamburger + Logo */}
          <div className="flex items-center gap-2 min-w-0 flex-shrink">
            <button
              data-testid="nav-hamburger-btn"
              onClick={() => setOpen(true)}
              className="p-2 -ml-1 rounded-lg hover:bg-secondary/50 transition-colors flex-shrink-0"
              aria-label="Open navigation menu"
            >
              <List weight="bold" size={22} />
            </button>
            <div className="flex items-center gap-2 cursor-pointer flex-shrink-0" onClick={() => navigate('/')}>
              <TurfPulseLogoSVG animated={true} size={28} />
              <div className="hidden sm:block">
                <h1 className="text-base font-bold leading-tight">TurfPulse™</h1>
              </div>
            </div>
            {/* Current page indicator */}
            {currentPage && (
              <span className="hidden lg:inline-flex items-center text-sm text-muted-foreground ml-2 pl-3 border-l border-border whitespace-nowrap">
                <span className="mr-1.5">{currentPage.icon}</span>
                {currentPage.label}
              </span>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            <button
              onClick={() => navigate('/profile')}
              className="text-sm text-muted-foreground hidden lg:block hover:text-foreground transition-colors cursor-pointer truncate max-w-[100px]"
              data-testid="nav-profile-link"
            >
              {user?.name}
            </button>

            {tierLabel && tierLabel !== 'Enthusiast' && user?.role !== 'admin' && (
              <span data-testid="tier-badge" className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium hidden lg:inline-flex">
                {tierLabel}
              </span>
            )}

            <LanguageSelector variant="compact" />

            <Button
              data-testid="theme-toggle-btn"
              size="sm"
              variant="ghost"
              onClick={toggleTheme}
              className="h-8 w-8 p-0 flex-shrink-0"
              aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? <Moon weight="bold" size={16} /> : <Sun weight="bold" size={16} />}
            </Button>

            {user?.role === 'admin' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/admin')}
                className="h-8 px-2 md:px-3 text-xs flex-shrink-0"
                data-testid="nav-admin-btn"
                aria-label="Admin panel"
              >
                <UserGear weight="bold" size={14} />
                <span className="hidden sm:inline ml-1">{t('nav:admin')}</span>
              </Button>
            )}

            {user?.subscription_tier === 'free' && user?.role !== 'admin' && (
              <Button
                data-testid="upgrade-btn"
                size="sm"
                variant="default"
                onClick={() => navigate('/app')}
                className="hidden md:flex h-8 text-xs flex-shrink-0"
              >
                <CreditCard weight="bold" size={14} className="mr-1" />
                {t('common:upgrade')}
              </Button>
            )}

            <Button
              size="sm"
              variant="ghost"
              onClick={handleLogout}
              className="h-8 w-8 p-0 md:w-auto md:px-2 flex-shrink-0"
              aria-label={t('common:logout')}
            >
              <SignOut weight="bold" size={14} />
              <span className="hidden lg:inline ml-1">{t('common:logout')}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 p-0 overflow-y-auto">
          <SheetHeader className="px-4 pt-5 pb-3 border-b border-border">
            <div className="flex items-center gap-2">
              <TurfPulseLogoSVG animated={false} size={28} />
              <div>
                <SheetTitle className="text-base font-bold leading-tight">TurfPulse™</SheetTitle>
                <SheetDescription className="text-xs">Smart Lawn Care, Backed by Science</SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <nav className="py-2" aria-label="Main navigation">
            {navGroups.map((group) => (
              <div key={group.label} className="mb-1">
                <div className="px-4 py-2">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">{group.label}</span>
                </div>
                {group.items.map((item) => {
                  const active = isActive(item.path);
                  const locked = isLocked(item.path);
                  return (
                    <button
                      key={item.path}
                      data-testid={`nav-item-${item.path.replace('/', '')}`}
                      onClick={() => handleNav(item.path)}
                      className={`
                        w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors
                        ${active
                          ? 'bg-primary/10 text-primary font-semibold border-l-2 border-primary'
                          : locked
                            ? 'text-muted-foreground/50 hover:bg-secondary/30'
                            : 'text-foreground/80 hover:bg-secondary/50 hover:text-foreground'
                        }
                      `}
                    >
                      <span className="text-base w-5 text-center">{item.icon}</span>
                      <span className="flex-1 text-left">{item.label}</span>
                      {locked && <Lock weight="bold" size={12} className="text-muted-foreground/40" />}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Drawer Footer */}
          <div className="border-t border-border px-4 py-3 mt-auto">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => { navigate('/profile'); setOpen(false); }}
                className="text-sm font-medium hover:text-primary transition-colors text-left"
                data-testid="drawer-profile-link"
              >
                {user?.name || user?.email}
              </button>
              {tierLabel && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  tierLabel === 'Enthusiast' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  : tierLabel === 'Homeowner' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'bg-muted text-muted-foreground'
                }`}>
                  {tierLabel}
                </span>
              )}
            </div>
            {user?.subscription_tier === 'free' && user?.role !== 'admin' && (
              <Button
                size="sm"
                variant="default"
                onClick={() => { navigate('/app'); setOpen(false); }}
                className="w-full h-8 text-xs mt-2 md:hidden"
              >
                <CreditCard weight="bold" size={14} className="mr-1" />
                Upgrade
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};
